import {
  Fragment,
  useState,
  useEffect,
  useRef,
  type Dispatch,
  type SetStateAction,
} from "react";
import invoiceAmountCheck from "../../assets/svg/invoice-amount-check.svg";
import invoiceGenerate from "../../assets/svg/invoice-generate.svg";
import DateTimePicker from "../../components/DateTimePicker";
import {
  getInvoicePreview,
  saveInvoice,
  generateInvoiceDocument,
  sendInvoiceToClient,
  getInvoiceSendToClientPreview,
  sendInvoicePaymentMessage,
} from "../../api/discussion";
import type {
  InvoicePerAppFeesDto,
  InvoicePreviewResponse,
  InvoiceTotalsSummaryDto,
} from "../../api/discussion";
import { getWorkflowStatus } from "api/applications";
import { toast } from "sonner";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { HiOutlineX, HiOutlineInformationCircle } from "react-icons/hi";
import { Checkbox } from "components/ui/checkbox";
import ConfirmMessageModal from "components/ConfirmMessageModal";
import { renderWhatsAppFormatting } from "lib/whatsappFormatting";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function formatInvoiceRupees(n: number): string {
  return `Rs.${Math.round(n).toLocaleString("en-IN")}/-`;
}

function formatInvoiceDateDisplay(iso: string | null): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}-${m}-${y}`;
}

function parsePreviewMessage(message: string): {
  header: string;
  rows: Array<{ label: string; value: string } | { text: string }>;
} {
  const lines = message.split("\n");
  let header = "";
  const rows: Array<{ label: string; value: string } | { text: string }> = [];

  let i = 0;
  for (; i < lines.length; i++) {
    if (lines[i].trim()) {
      header = lines[i].trim();
      i++;
      break;
    }
  }

  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) {
      i++;
      continue;
    }
    const idx = line.indexOf(":");
    if (idx > 0) {
      const label = line.slice(0, idx).trim();
      const inlineValue = line.slice(idx + 1).trim();
      if (inlineValue) {
        rows.push({ label, value: inlineValue });
        i++;
        continue;
      }
      let j = i + 1;
      while (j < lines.length && !lines[j].trim()) j++;
      if (j < lines.length) {
        rows.push({ label, value: lines[j].trim() });
        i = j + 1;
        continue;
      }
      rows.push({ label, value: "" });
      i++;
      continue;
    }
    rows.push({ text: line });
    i++;
  }

  return { header, rows };
}

function PaymentMessageBlock({ message }: { message: string }) {
  const { header, rows } = parsePreviewMessage(message);
  return (
    <div className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-2 gap-y-1.5 font-inter text-[13px] leading-relaxed text-[#222]">
      {header && (
        <p className="col-span-3 m-0 mb-2 break-words font-medium">
          {renderWhatsAppFormatting(header)}
        </p>
      )}
      {rows.map((row, i) =>
        "label" in row ? (
          <Fragment key={i}>
            <div className="break-words text-[#222]">
              {renderWhatsAppFormatting(row.label)}
            </div>
            <div className="text-[#222]">:</div>
            <div className="min-w-0 break-words text-[#222]">
              {renderWhatsAppFormatting(row.value)}
            </div>
          </Fragment>
        ) : (
          <p key={i} className="col-span-3 m-0 break-words">
            {renderWhatsAppFormatting(row.text)}
          </p>
        )
      )}
    </div>
  );
}

function InvoicePreviewRow({
  label,
  value,
  emphasize = false,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-baseline gap-x-2 text-[13px] leading-relaxed font-inter sm:grid-cols-[220px_auto_minmax(0,1fr)] md:grid-cols-[260px_auto_minmax(0,1fr)]">
      <span className="font-semibold text-[#101111] break-words">{label}</span>
      <span className="text-[#101111]">:</span>
      <span
        className={`break-words whitespace-pre-line ${emphasize ? "font-semibold text-[#101111]" : "text-[#222222]"}`}
      >
        {value}
      </span>
    </div>
  );
}

/** Page 37–style summary; data from GET invoice preview (same as PDF). */
function InvoicePage37Preview({ data }: { data: InvoicePreviewResponse }) {
  const ts = data.totalsSummary;
  const addresseeValue =
    [
      data.addressee.designation,
      data.addressee.companyName,
      data.addressee.location,
    ]
      .filter((v) => v && String(v).trim().length > 0)
      .join("\n") || "—";

  return (
    <div className="mx-auto max-w-2xl flex flex-col gap-2 px-1 py-2 font-inter">
      <p className="border-b border-[#EEEEEE] pb-2 text-sm font-semibold text-[#101111]">
        Data : Invoice for New Filing
      </p>
      <InvoicePreviewRow label="Addressee" value={addresseeValue} />
      <InvoicePreviewRow
        label="Form of Addressee"
        value={data.formOfAddressee ?? "—"}
      />
      <InvoicePreviewRow label="Mark" value={data.markName ?? "—"} />
      <InvoicePreviewRow
        label={`Selected ${data.selectedClassesLabel}`}
        value={data.selectedClasses ?? "—"}
      />
      <InvoicePreviewRow
        label="Selected Type of Mark"
        value={data.selectedTypeOfMark ?? "—"}
      />
      <InvoicePreviewRow
        label="No of Applications"
        value={String(ts?.noOfApplications ?? "—")}
      />
      <InvoicePreviewRow
        label="Expedited Application"
        value={data.expeditedApplication ? "YES" : "NO"}
      />
      <InvoicePreviewRow
        label="Govt Fee for New filing"
        value={formatInvoiceRupees(ts?.govtFeeTotal ?? 0)}
      />
      <InvoicePreviewRow
        label="Govt Fee for Expedited Process"
        value={formatInvoiceRupees(ts?.expeditedFeeTotal ?? 0)}
      />
      <InvoicePreviewRow
        label="Discount in Prof Fee for New filing"
        value={formatInvoiceRupees(ts?.discountTotal ?? 0)}
      />
      <InvoicePreviewRow
        label="Prof Fee for New filing"
        value={formatInvoiceRupees(ts?.profFeeTotal ?? 0)}
      />
      <InvoicePreviewRow
        label="Collected Advance Amount"
        value={formatInvoiceRupees(ts?.collectedAdvance ?? 0)}
      />
      <div className="my-1 border-t border-dashed border-[#EEEEEE]" />
      <InvoicePreviewRow
        label="Total Charges"
        value={formatInvoiceRupees(ts?.totalChargesBeforeDeductions ?? 0)}
      />
      <InvoicePreviewRow
        label="Invoice Amount for New filing"
        value={formatInvoiceRupees(ts?.invoiceAmount ?? 0)}
        emphasize
      />
      <div className="my-1 border-t border-dashed border-[#EEEEEE]" />
      <InvoicePreviewRow
        label="Invoice No. for New Filing"
        value={data.invoiceNo ?? "—"}
      />
      <InvoicePreviewRow
        label="Date of Invoice for New Filing"
        value={formatInvoiceDateDisplay(data.invoiceDate)}
      />
    </div>
  );
}

type InvoicePreviewTab = "summary" | "pdf" | "message";

function InvoicePreviewTabsPanel({
  data,
  pdfUrl,
  previewTab,
  setPreviewTab,
  numPages,
  setNumPages,
  pdfMaxWidth = 800,
  embedInDialog = true,
  paymentMessage,
  paymentMessageLoading = false,
}: {
  data: InvoicePreviewResponse;
  pdfUrl: string | null;
  previewTab: InvoicePreviewTab;
  setPreviewTab: Dispatch<SetStateAction<InvoicePreviewTab>>;
  numPages: number | null;
  setNumPages: (n: number | null) => void;
  pdfMaxWidth?: number;
  embedInDialog?: boolean;
  paymentMessage?: string | null;
  paymentMessageLoading?: boolean;
}) {
  const pageWidth = Math.min(
    pdfMaxWidth,
    typeof window !== "undefined" ? window.innerWidth - 48 : pdfMaxWidth
  );

  return (
    <div
      className={`flex min-h-0 flex-col ${embedInDialog ? "" : "h-full min-h-0"}`}
    >
      <div
        className="flex shrink-0 gap-1 border-b border-[#e3e3e3] bg-white px-2 sm:px-3"
        role="tablist"
      >
        <button
          type="button"
          role="tab"
          aria-selected={previewTab === "summary"}
          onClick={() => setPreviewTab("summary")}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
            previewTab === "summary"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Invoice details
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={previewTab === "pdf"}
          onClick={() => setPreviewTab("pdf")}
          disabled={!pdfUrl}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation disabled:cursor-not-allowed disabled:opacity-50 ${
            previewTab === "pdf"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Generated PDF
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={previewTab === "message"}
          onClick={() => setPreviewTab("message")}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
            previewTab === "message"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Message Preview
        </button>
      </div>
      <div
        className={
          embedInDialog
            ? "max-h-[55vh] min-h-0 flex-1 overflow-auto bg-[#fafafa] p-2 sm:p-3 md:max-h-[min(480px,55vh)]"
            : "min-h-0 flex-1 overflow-auto bg-[#fafafa] p-2 sm:p-4"
        }
      >
        {previewTab === "summary" ? (
          <div className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
            <InvoicePage37Preview data={data} />
          </div>
        ) : previewTab === "pdf" ? (
          pdfUrl ? (
            <Document
              file={pdfUrl}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              loading={
                <p className="text-sm text-[#6d6d6d] font-inter">Loading PDF…</p>
              }
              error={
                <p className="text-sm text-red-600 font-inter">Failed to load PDF.</p>
              }
              className="flex flex-col items-center gap-4"
            >
              {numPages != null &&
                Array.from({ length: numPages }, (_, i) => (
                  <Page
                    key={i + 1}
                    pageNumber={i + 1}
                    width={pageWidth}
                    renderTextLayer
                    renderAnnotationLayer
                  />
                ))}
            </Document>
          ) : (
            <p className="text-sm text-[#6d6d6d] font-inter">No PDF URL available.</p>
          )
        ) : (
          <div className="rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5">
            {paymentMessageLoading ? (
              <p className="text-sm text-[#6d6d6d] font-inter">Loading preview…</p>
            ) : paymentMessage ? (
              <PaymentMessageBlock message={paymentMessage} />
            ) : (
              <p className="text-sm text-[#6d6d6d] font-inter">
                Payment message preview not available.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FormField({
  label,
  placeholder = "-",
  value,
  onChange,
  readOnly = false,
}: {
  label: string;
  placeholder?: string;
  value: string;
  onChange?: (v: string) => void;
  readOnly?: boolean;
}) {
  return (
    <div className="flex flex-1 min-w-0 flex-col gap-1 w-full">
      <label
        className="text-xs font-normal leading-5 text-[#222222]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={readOnly ? undefined : (e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
        className="h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] md:text-xs leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:border-[#222222] focus:outline-none focus:ring-1 focus:ring-[#222222] disabled:bg-[#F9FAFB] disabled:cursor-default"
        style={{ fontFamily: "Inter, sans-serif" }}
      />
    </div>
  );
}

type InvoiceToClientFinalViewProps = {
  leadId: string | null;
  onSuccess?: (leadId: string) => void;
  onSavingChange?: (saving: boolean) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

export default function InvoiceToClientFinalView({
  leadId,
  onSuccess,
  onSavingChange,
  onSentStatusChange,
}: InvoiceToClientFinalViewProps) {
  const [loading, setLoading] = useState(!!leadId);
  const [invoiceNo, setInvoiceNo] = useState("");
  const [mark, setMark] = useState("");
  const [govtFee, setGovtFee] = useState("");
  const [discountProfFee, setDiscountProfFee] = useState("");
  const [collectedAdvance, setCollectedAdvance] = useState("");
  const [noOfApplications, setNoOfApplications] = useState("");
  const [expeditedApplication, setExpeditedApplication] = useState("");
  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [dateOfInvoice, setDateOfInvoice] = useState<Date | null>(null);
  const [includeInvoiceNo, setIncludeInvoiceNo] = useState(false);

  const [invoiceTotals, setInvoiceTotals] = useState<InvoiceTotalsSummaryDto | null>(null);
  const [perAppFees, setPerAppFees] = useState<InvoicePerAppFeesDto | null>(null);
  const [calcTooltipOpen, setCalcTooltipOpen] = useState(false);
  const calcTooltipRef = useRef<HTMLDivElement>(null);

  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  const [generating, setGenerating] = useState(false);

  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [invoicePreviewData, setInvoicePreviewData] =
    useState<InvoicePreviewResponse | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState<InvoicePreviewTab>("summary");
  const [paymentMessageText, setPaymentMessageText] = useState<string | null>(null);
  const [paymentMessageLoading, setPaymentMessageLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [hasSentWhatsapp, setHasSentWhatsapp] = useState(false);
  const [resending, setResending] = useState(false);
  const [whatsappConfirmOpen, setWhatsappConfirmOpen] = useState(false);
  const [whatsappConfirmMode, setWhatsappConfirmMode] = useState<"send" | "resend">(
    "send"
  );

  useEffect(() => {
    if (!calcTooltipOpen) return;
    const handler = (e: MouseEvent) => {
      if (calcTooltipRef.current && !calcTooltipRef.current.contains(e.target as Node)) {
        setCalcTooltipOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [calcTooltipOpen]);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getInvoicePreview(leadId)
      .then((preview) => {
        if (cancelled) return;
        const totals = preview.totalsSummary;
        const perApp = preview.perAppFees;
        setInvoiceNo(preview.invoiceNo ?? "");
        setMark(preview.markName ?? "");
        setGovtFee(
          totals?.govtFeeTotal != null
            ? `Rs. ${totals.govtFeeTotal.toLocaleString("en-IN")}/-`
            : perApp?.govtFeePerApp != null
              ? `Rs. ${perApp.govtFeePerApp.toLocaleString("en-IN")}/-`
              : ""
        );
        setDiscountProfFee(
          perApp?.discountInProfFee != null
            ? String(perApp.discountInProfFee)
            : ""
        );
        setCollectedAdvance(
          totals?.collectedAdvance != null
            ? `Rs. ${totals.collectedAdvance.toLocaleString("en-IN")}/-`
            : ""
        );
        setNoOfApplications(
          totals?.noOfApplications != null
            ? String(totals.noOfApplications)
            : ""
        );
        setExpeditedApplication(
          preview.expeditedApplication ? "YES" : "NO"
        );
        setInvoiceAmount(
          totals?.invoiceAmount != null
            ? `Rs. ${totals.invoiceAmount.toLocaleString("en-IN")}/-`
            : ""
        );
        if (totals) setInvoiceTotals(totals);
        if (perApp) setPerAppFees(perApp);
        setDateOfInvoice(
          preview.invoiceDate
            ? new Date(preview.invoiceDate + "T12:00:00")
            : null
        );
        setIncludeInvoiceNo(Boolean(preview.includeInvoiceNo));
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load invoice details"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    if (!leadId) {
      setHasSentWhatsapp(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const status = await getWorkflowStatus(leadId);
        // "Resend to WhatsApp" must depend on the step that indicates WhatsApp was sent.
        // In backend status, the WhatsApp-sent marker appears as `S1_11B_INVOICE_SENT_TO_CLIENT`.
        const step = status.steps.find(
          (s: any) => s.slug === "S1_11B_INVOICE_SENT_TO_CLIENT"
        );
        if (!cancelled && step) {
          setHasSentWhatsapp(step.isCompleted);
          onSentStatusChange?.(step.isCompleted);
        }
      } catch {
        if (!cancelled) {
          setHasSentWhatsapp(false);
          onSentStatusChange?.(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, onSentStatusChange]);

  const getInvoiceDateStr = () =>
    dateOfInvoice
      ? `${dateOfInvoice.getFullYear()}-${String(dateOfInvoice.getMonth() + 1).padStart(2, "0")}-${String(dateOfInvoice.getDate()).padStart(2, "0")}`
      : undefined;

  /** Sync list/detail fields from API preview (invoice no/date after save). */
  const applyInvoicePreviewToForm = (preview: InvoicePreviewResponse) => {
    const totals = preview.totalsSummary;
    const perApp = preview.perAppFees;
    setInvoiceNo(preview.invoiceNo ?? "");
    setMark(preview.markName ?? "");
    setGovtFee(
      totals?.govtFeeTotal != null
        ? `Rs. ${totals.govtFeeTotal.toLocaleString("en-IN")}/-`
        : perApp?.govtFeePerApp != null
          ? `Rs. ${perApp.govtFeePerApp.toLocaleString("en-IN")}/-`
          : ""
    );
    setDiscountProfFee(
      perApp?.discountInProfFee != null
        ? String(perApp.discountInProfFee)
        : ""
    );
    setCollectedAdvance(
      totals?.collectedAdvance != null
        ? `Rs. ${totals.collectedAdvance.toLocaleString("en-IN")}/-`
        : ""
    );
    setNoOfApplications(
      totals?.noOfApplications != null ? String(totals.noOfApplications) : ""
    );
    setExpeditedApplication(preview.expeditedApplication ? "YES" : "NO");
    setInvoiceAmount(
      totals?.invoiceAmount != null
        ? `Rs. ${totals.invoiceAmount.toLocaleString("en-IN")}/-`
        : ""
    );
    if (totals) setInvoiceTotals(totals);
    setDateOfInvoice(
      preview.invoiceDate
        ? new Date(preview.invoiceDate + "T12:00:00")
        : null
    );
    setIncludeInvoiceNo(Boolean(preview.includeInvoiceNo));
  };

  const prepareInvoiceForWhatsAppConfirm = async (): Promise<boolean> => {
    if (!leadId) return false;
    try {
      await saveInvoice(leadId, {
        invoiceDate: getInvoiceDateStr(),
        includeInvoiceNo,
        profFeeDiscount: discountProfFee.trim() === "" ? 0 : Number(discountProfFee) || 0,
      });
      const [preview, { downloadUrl }] = await Promise.all([
        getInvoicePreview(leadId),
        generateInvoiceDocument(leadId),
      ]);
      applyInvoicePreviewToForm(preview);
      setInvoicePreviewData(preview);
      setPreviewPdfUrl(downloadUrl);
      setPreviewTab("summary");
      setNumPages(null);
      setPaymentMessageText(null);
      setPaymentMessageLoading(true);
      getInvoiceSendToClientPreview(leadId)
        .then((sendPreview) => {
          setPaymentMessageText(sendPreview.paymentMessagePreview ?? null);
        })
        .catch(() => {
          setPaymentMessageText(null);
        })
        .finally(() => {
          setPaymentMessageLoading(false);
        });
      return true;
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to prepare invoice for sending"
      );
      return false;
    }
  };

  const handleConfirmWhatsAppSend = () => {
    if (!leadId) return;
    setSaving(true);
    onSavingChange?.(true);
    void (async () => {
      try {
        const [invoiceResult, paymentResult] = await Promise.allSettled([
          sendInvoiceToClient(leadId),
          sendInvoicePaymentMessage(leadId),
        ]);
        const invoiceOk = invoiceResult.status === "fulfilled";
        const paymentOk = paymentResult.status === "fulfilled";
        const invoiceErr =
          invoiceResult.status === "rejected"
            ? invoiceResult.reason instanceof Error
              ? invoiceResult.reason.message
              : "Failed to send invoice to client"
            : null;
        const paymentErr =
          paymentResult.status === "rejected"
            ? paymentResult.reason instanceof Error
              ? paymentResult.reason.message
              : "Failed to send payment message"
            : null;
        const isResend = whatsappConfirmMode === "resend";

        if (invoiceOk && paymentOk) {
          setHasSentWhatsapp(true);
          onSentStatusChange?.(true);
          const message = isResend
            ? "Invoice resent to client successfully."
            : "Invoice saved and sent to client successfully.";
          toast.success(message);
          setSaveSuccess(message);
          onSuccess?.(leadId);
        } else if (invoiceOk && !paymentOk) {
          setHasSentWhatsapp(true);
          onSentStatusChange?.(true);
          const warning = `Invoice sent to client, but payment message failed: ${paymentErr}`;
          toast.warning(warning);
          setSaveSuccess(warning);
          onSuccess?.(leadId);
        } else if (!invoiceOk && paymentOk) {
          toast.warning(
            `Payment message sent, but sending invoice failed: ${invoiceErr}`
          );
        } else {
          toast.error(invoiceErr ?? "Failed to send invoice to client");
        }
      } finally {
        setSaving(false);
        onSavingChange?.(false);
      }
    })();
  };

  const closeWhatsAppConfirm = () => {
    setWhatsappConfirmOpen(false);
    setInvoicePreviewData(null);
    setPreviewPdfUrl(null);
    setNumPages(null);
    setPreviewTab("summary");
    setPaymentMessageText(null);
    setPaymentMessageLoading(false);
  };

  const closePreviewModal = () => {
    setPreviewModalOpen(false);
    setInvoicePreviewData(null);
    setPreviewPdfUrl(null);
    setNumPages(null);
    setPreviewTab("summary");
    setPaymentMessageText(null);
    setPaymentMessageLoading(false);
  };

  const handleSaveAndContinue = async () => {
    if (!leadId || saving) return;
    setSaveSuccess(null);

    if (hasSentWhatsapp) {
      setSaving(true);
      onSavingChange?.(true);
      try {
        await saveInvoice(leadId, {
          invoiceDate: getInvoiceDateStr(),
          includeInvoiceNo,
        });
        await generateInvoiceDocument(leadId);
        const preview = await getInvoicePreview(leadId);
        applyInvoicePreviewToForm(preview);
        toast.success("Invoice saved successfully.");
        setSaveSuccess("Invoice saved successfully.");
        onSuccess?.(leadId);
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to save invoice"
        );
      } finally {
        setSaving(false);
        onSavingChange?.(false);
      }
      return;
    }

    setWhatsappConfirmOpen(false);
    setPreviewModalOpen(false);
    setSaving(true);
    onSavingChange?.(true);
    try {
      const ok = await prepareInvoiceForWhatsAppConfirm();
      if (ok) {
        setWhatsappConfirmMode("send");
        setWhatsappConfirmOpen(true);
      }
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!leadId) return;
    setGenerating(true);
    try {
      await saveInvoice(leadId, {
        invoiceDate: getInvoiceDateStr(),
        includeInvoiceNo,
        profFeeDiscount: discountProfFee.trim() === "" ? 0 : Number(discountProfFee) || 0,
      });
      await generateInvoiceDocument(leadId);
      toast.success("Invoice PDF generated successfully.");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!leadId) return;
    setGenerating(true);
    try {
      // Persist latest invoice form values before generating download.
      await saveInvoice(leadId, {
        invoiceDate: getInvoiceDateStr(),
        includeInvoiceNo,
        profFeeDiscount: discountProfFee.trim() === "" ? 0 : Number(discountProfFee) || 0,
      });
      const { downloadUrl, filename } = await generateInvoiceDocument(leadId);
      const result = await downloadFileFromUrlWithFallback(
        downloadUrl,
        filename ?? "invoice.pdf"
      );
      if (result === "local") {
        toast.success("PDF saved to your device.");
      } else {
        toast.info(
          "Opened the PDF in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to download"
      );
    } finally {
      setGenerating(false);
    }
  };

  const handlePreview = () => {
    if (!leadId) return;
    setWhatsappConfirmOpen(false);
    setInvoicePreviewData(null);
    setPreviewPdfUrl(null);
    setNumPages(null);
    setPreviewTab("summary");
    setPreviewModalOpen(true);
    setPreviewing(true);
    // TC_99: "Preview" must open a data-only preview window — NO PDF is generated
    // at this stage. PDF generation is deferred to the explicit "Generate PDF"
    // confirmation inside the preview modal (handleGenerateFromPreview).
    void (async () => {
      try {
        // Persist latest form values so the preview reflects current edits.
        await saveInvoice(leadId, {
          invoiceDate: getInvoiceDateStr(),
          includeInvoiceNo,
          profFeeDiscount: discountProfFee.trim() === "" ? 0 : Number(discountProfFee) || 0,
        });
        const preview = await getInvoicePreview(leadId);
        applyInvoicePreviewToForm(preview);
        setInvoicePreviewData(preview);
        setPaymentMessageText(null);
        setPaymentMessageLoading(true);
        getInvoiceSendToClientPreview(leadId)
          .then((sendPreview) => {
            setPaymentMessageText(sendPreview.paymentMessagePreview ?? null);
          })
          .catch(() => {
            setPaymentMessageText(null);
          })
          .finally(() => {
            setPaymentMessageLoading(false);
          });
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to load invoice preview"
        );
        setPreviewModalOpen(false);
      } finally {
        setPreviewing(false);
      }
    })();
  };

  // TC_99: confirmation step — generate the PDF on demand from the preview
  // window, then reveal it in the "Generated PDF" tab.
  const handleGenerateFromPreview = async () => {
    if (!leadId) return;
    setGenerating(true);
    try {
      const { downloadUrl } = await generateInvoiceDocument(leadId);
      setPreviewPdfUrl(downloadUrl);
      setNumPages(null);
      setPreviewTab("pdf");
      toast.success("Invoice PDF generated successfully.");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
    } finally {
      setGenerating(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex w-full max-w-[1085px] flex-col gap-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-xs text-[#6D6D6D]">
          Please complete the previous steps to access this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex w-full max-w-[1085px] items-center justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading invoice details...</p>
      </div>
    );
  }

  // Live recomputation: when the user edits the discount, recompute totals on
  // the client so the Invoice Amount + breakdown tooltip update immediately
  // (backend recomputes the same way on save).
  const liveDiscountPerApp =
    discountProfFee.trim() === "" ? 0 : Math.max(0, Number(discountProfFee) || 0);
  const liveInvoiceTotals = invoiceTotals
    ? (() => {
        const liveDiscountTotal =
          liveDiscountPerApp * invoiceTotals.noOfApplications;
        const liveInvoiceAmount = Math.max(
          0,
          Math.round(
            invoiceTotals.totalChargesBeforeDeductions -
              liveDiscountTotal -
              invoiceTotals.collectedAdvance
          )
        );
        return {
          ...invoiceTotals,
          discountTotal: liveDiscountTotal,
          invoiceAmount: liveInvoiceAmount,
        };
      })()
    : null;
  const displayInvoiceAmount = liveInvoiceTotals
    ? `Rs. ${liveInvoiceTotals.invoiceAmount.toLocaleString("en-IN")}/-`
    : invoiceAmount || "Rs. 0/-";

  return (
    <form
      id="invoice-final-form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSaveAndContinue();
      }}
      className="flex w-full max-w-[1085px] flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {saving && <p className="text-xs text-[#6D6D6D]">Saving...</p>}
      {saveSuccess && <p className="text-xs text-green-600">{saveSuccess}</p>}

      {/* Row 1 (editable + computed): Date | Invoice Amount | Discount */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <div className="flex min-w-0 flex-col gap-1 w-full">
          <label
            className="text-xs font-normal leading-5 text-[#222222]"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Date of Invoice for New Filing
          </label>
          <DateTimePicker
            value={dateOfInvoice}
            onChange={(d) => setDateOfInvoice(d)}
            buttonClassName="min-h-[44px] md:min-h-0 rounded-[6px] text-[12px] md:text-xs"
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1 w-full">
          <div className="flex items-center gap-1">
            <label
              className="text-xs font-normal leading-5 text-[#222222]"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              Invoice Amount for New filing
            </label>
            {liveInvoiceTotals && (
              <div className="relative" ref={calcTooltipRef}>
                <button
                  type="button"
                  onClick={() => setCalcTooltipOpen((o) => !o)}
                  className="flex items-center justify-center text-[#6D6D6D] hover:text-[#222222]"
                  aria-label="Show calculation"
                >
                  <HiOutlineInformationCircle className="w-4 h-4" strokeWidth={1.5} />
                </button>
                {calcTooltipOpen && (
                  <div className="absolute left-0 top-full mt-1 z-30 w-72 rounded-[8px] border border-[#EEEEEE] bg-white shadow-lg p-3 text-[12px] leading-5 text-[#222222]" style={{ fontFamily: "Inter, sans-serif" }}>
                    <p className="text-xs font-semibold text-[#101111] mb-2">Calculation Breakdown</p>
                    <div className="flex flex-col gap-1">
                      {(() => {
                        const n = liveInvoiceTotals.noOfApplications;
                        const appLabel = `${n} app${n !== 1 ? "s" : ""}`;
                        const perApp = (rate: number) =>
                          ` (${appLabel} × Rs. ${rate.toLocaleString("en-IN")})`;
                        return (
                          <>
                            <div className="flex justify-between gap-3">
                              <span className="text-[#6D6D6D]">
                                Govt Fee{perAppFees ? perApp(perAppFees.govtFeePerApp) : ` (${appLabel})`}
                              </span>
                              <span className="shrink-0">Rs. {liveInvoiceTotals.govtFeeTotal.toLocaleString("en-IN")}/-</span>
                            </div>
                            {liveInvoiceTotals.expeditedFeeTotal > 0 && (
                              <div className="flex justify-between gap-3">
                                <span className="text-[#6D6D6D]">
                                  Expedited Fee{perAppFees ? perApp(perAppFees.expeditedFeePerApp) : ""}
                                </span>
                                <span className="shrink-0">Rs. {liveInvoiceTotals.expeditedFeeTotal.toLocaleString("en-IN")}/-</span>
                              </div>
                            )}
                            <div className="flex justify-between gap-3">
                              <span className="text-[#6D6D6D]">
                                Prof Fee{perAppFees ? perApp(perAppFees.profFeePerApp) : ""}
                              </span>
                              <span className="shrink-0">Rs. {liveInvoiceTotals.profFeeTotal.toLocaleString("en-IN")}/-</span>
                            </div>
                            <div className="flex justify-between gap-3 text-red-600">
                              <span>
                                − Discount{liveDiscountPerApp > 0 ? perApp(liveDiscountPerApp) : ""}
                              </span>
                              <span className="shrink-0">Rs. {liveInvoiceTotals.discountTotal.toLocaleString("en-IN")}/-</span>
                            </div>
                          </>
                        );
                      })()}
                      <div className="border-t border-[#EEE] pt-1 mt-1 flex justify-between font-medium">
                        <span className="text-[#6D6D6D]">Total Charges</span>
                        <span>Rs. {liveInvoiceTotals.totalChargesBeforeDeductions.toLocaleString("en-IN")}/-</span>
                      </div>
                      <div className="flex justify-between text-red-600">
                        <span>− Collected Advance</span>
                        <span>Rs. {liveInvoiceTotals.collectedAdvance.toLocaleString("en-IN")}/-</span>
                      </div>
                      <div className="border-t border-[#222] pt-1 mt-1 flex justify-between font-semibold text-[#101111]">
                        <span>Invoice Amount</span>
                        <span>Rs. {liveInvoiceTotals.invoiceAmount.toLocaleString("en-IN")}/-</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <input
            type="text"
            value={displayInvoiceAmount}
            readOnly
            className="h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] md:text-xs leading-5 text-[#222222] focus:border-[#222222] focus:outline-none focus:ring-1 focus:ring-[#222222]"
            style={{ fontFamily: "Inter, sans-serif" }}
          />
        </div>
        <FormField
          label="Discount in Prof Fee for New filing"
          value={discountProfFee}
          onChange={(v) => setDiscountProfFee(v.replace(/[^\d.]/g, ""))}
          placeholder="0"
        />
      </div>

      {/* Row 2 (read-only context): Invoice No. | Mark | Govt Fee */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <FormField
          label="Invoice No. for New Filing"
          placeholder="Auto-generated on save"
          value={invoiceNo}
          readOnly
        />
        <FormField label="Mark" value={mark} readOnly />
        <FormField
          label="Govt Fee for New filing"
          value={govtFee}
          readOnly
        />
      </div>

      {/* Row 3 (read-only context): Collected Advance | No of Applications | Expedited */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <FormField
          label="Collected Advance Amount"
          value={collectedAdvance}
          readOnly
        />
        <FormField
          label="No of Applications"
          value={noOfApplications}
          readOnly
        />
        <FormField
          label="Expedited Application : YES or NO"
          value={expeditedApplication}
          readOnly
        />
      </div>

      {/* Invoice Amount summary bar */}
      <div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 rounded-[8px] px-4 sm:px-5 py-4 sm:py-0 min-h-[72px] sm:h-[72px]"
        style={{ background: "#222222" }}
      >
        <div className="flex flex-row items-center gap-2 min-w-0">
          <img
            src={invoiceAmountCheck}
            alt=""
            className="w-5 h-5 flex-shrink-0"
          />
          <span
            className="text-[12px] md:text-sm font-normal leading-5 text-white/90 truncate"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            INVOICE AMOUNT FOR NEW FILING
          </span>
        </div>
        <span
          className="text-lg md:text-[22px] font-bold leading-8 md:leading-10 text-white shrink-0"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {displayInvoiceAmount}
        </span>
      </div>

      {/* Generate PDF + Download + Preview */}
      <div className="flex items-center gap-2">
        <Checkbox
          id="include-invoice-no"
          checked={includeInvoiceNo}
          onCheckedChange={(checked) => setIncludeInvoiceNo(Boolean(checked))}
        />
        <label
          htmlFor="include-invoice-no"
          className="text-xs md:text-sm text-[#222222] cursor-pointer"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          Include Invoice No. in PDF
        </label>
      </div>
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5">
        <div className="flex flex-row items-center gap-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] pl-4 pr-2 py-2 min-h-[44px] sm:h-11">
          <img
            src={invoiceGenerate}
            alt=""
            className="w-[22px] h-[22px] flex-shrink-0"
          />
          <button
            type="button"
            onClick={handleGeneratePdf}
            disabled={generating}
            className="flex flex-row items-center justify-center min-h-[40px] h-9 px-4 rounded-[6px] bg-[#222222] text-[12px] md:text-xs font-medium text-white hover:bg-[#333333] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            {generating ? "Generating..." : "Generate PDF"}
          </button>
        </div>
        <button
          type="button"
          onClick={handleDownload}
          disabled={generating}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          Download
        </button>
        <button
          type="button"
          onClick={handlePreview}
          disabled={previewing}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {previewing ? "Generating..." : "Preview"}
        </button>
      </div>

      <ConfirmMessageModal
        open={whatsappConfirmOpen && !!invoicePreviewData}
        onClose={closeWhatsAppConfirm}
        title="Confirm invoice to client"
        previewSlot={
          invoicePreviewData ? (
            <InvoicePreviewTabsPanel
              data={invoicePreviewData}
              pdfUrl={previewPdfUrl}
              previewTab={previewTab}
              setPreviewTab={setPreviewTab}
              numPages={numPages}
              setNumPages={setNumPages}
              pdfMaxWidth={640}
              paymentMessage={paymentMessageText}
              paymentMessageLoading={paymentMessageLoading}
            />
          ) : null
        }
        onConfirm={handleConfirmWhatsAppSend}
        disableConfirm={saving}
      />

      {previewModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4">
          <div className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-3 py-3 sm:px-4">
              <span
                className="truncate pr-2 text-xs font-medium text-[#222] sm:text-sm font-inter"
              >
                Invoice preview
              </span>
              <button
                type="button"
                onClick={closePreviewModal}
                className="flex size-10 shrink-0 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] sm:size-8 touch-manipulation"
                aria-label="Close"
              >
                <HiOutlineX className="size-5" strokeWidth={2} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden p-2 sm:p-4">
              {invoicePreviewData ? (
                <InvoicePreviewTabsPanel
                  data={invoicePreviewData}
                  pdfUrl={previewPdfUrl}
                  previewTab={previewTab}
                  setPreviewTab={setPreviewTab}
                  numPages={numPages}
                  setNumPages={setNumPages}
                  pdfMaxWidth={800}
                  embedInDialog={false}
                  paymentMessage={paymentMessageText}
                  paymentMessageLoading={paymentMessageLoading}
                />
              ) : (
                <p className="text-sm text-[#6d6d6d] font-inter">
                  Preparing invoice preview…
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center justify-end gap-2.5 border-t border-[#e3e3e3] px-3 py-3 sm:px-4">
              <button
                type="button"
                onClick={closePreviewModal}
                className="flex items-center justify-center min-h-[40px] h-9 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors touch-manipulation font-inter"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleGenerateFromPreview}
                disabled={generating || !invoicePreviewData}
                className="flex items-center justify-center min-h-[40px] h-9 px-4 rounded-[6px] bg-[#222222] text-[12px] md:text-xs font-medium text-white hover:bg-[#333333] transition-colors disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation font-inter"
              >
                {generating
                  ? "Generating..."
                  : previewPdfUrl
                    ? "Regenerate PDF"
                    : "Generate PDF"}
              </button>
            </div>
          </div>
        </div>
      )}

      {hasSentWhatsapp && leadId && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={resending}
            onClick={async () => {
              if (!leadId || resending) return;
              setPreviewModalOpen(false);
              setResending(true);
              onSavingChange?.(true);
              try {
                const ok = await prepareInvoiceForWhatsAppConfirm();
                if (ok) {
                  setWhatsappConfirmMode("resend");
                  setWhatsappConfirmOpen(true);
                }
              } finally {
                setResending(false);
                onSavingChange?.(false);
              }
            }}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Preparing…" : "Resend to WhatsApp"}
          </button>
        </div>
      )}
    </form>
  );
}
